import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import CustomLink from '../../CustomLink';
import BfHeader from './BfHeader';
import useReviewStats from './useReviewStats';
import GiftRoll from './GiftRoll';
import Usps from './Usps';
import useLeadSignup from './useLeadSignup';
import useDeadline from './useDeadline';
import HeroPhoto from '../../../assets/bf-hero-lifestyle.webp';
import FormulaVideo from '../../../assets/personalization-formula.mp4';
import FormulaPoster from '../../../assets/personalization-formula-poster.webp';
import FoundersImage from '../../../assets/ale-and-paul.webp';
import './BlackFriday.css';

const LIST_DEADLINE = '2026-11-06 23:59:00';
const KEY = 'BF_LG_';

const HERO_BULLETS = ['Bullet1', 'Bullet2', 'Bullet3'];
const MARQUEE_REVIEWS = ['Review1', 'Review2', 'Review3', 'Review4'];
const PERKS = ['Perk1', 'Perk2', 'Perk3', 'Perk4'];
const PRODUCT_POINTS = ['Product1', 'Product2', 'Product4'];
const HOW_STEPS = ['How1', 'How2', 'How3'];
const FAQS = ['Faq1', 'Faq2', 'Faq3', 'Faq4', 'Faq5'];

const Stars = () => (
  <span className="bflg-stars" aria-hidden="true">
    {[0, 1, 2, 3, 4].map((star) => (
      <svg key={star} viewBox="0 0 24 24" width="16" height="16">
        <path d="M12 2l2.9 6.9 7.1.6-5.4 4.7 1.6 7.3L12 17.7 5.8 21.5l1.6-7.3L2 9.5l7.1-.6z" />
      </svg>
    ))}
  </span>
);

const pad = (value) => String(value).padStart(2, '0');

const Countdown = ({ remaining, t }) => {
  const units = [
    ['days', 'Days'],
    ['hours', 'Hours'],
    ['minutes', 'Minutes'],
    ['seconds', 'Seconds'],
  ];
  return (
    <div className="bflg-countdown" role="timer" aria-live="off">
      {units.map(([unit, label]) => (
        <div className="bflg-countdown__unit" key={unit}>
          <strong>{pad(remaining[unit])}</strong>
          <span>{t(`${KEY}${label}`)}</span>
        </div>
      ))}
    </div>
  );
};

const SignupForm = ({ formRef }) => {
  const { t } = useTranslation();
  const { status, validate, submit } = useLeadSignup();
  const remaining = useDeadline(LIST_DEADLINE);
  const [values, setValues] = useState({ firstName: '', email: '' });
  const [errors, setErrors] = useState({});
  const submitting = status === 'submitting';

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length === 0) submit(values);
  };

  return (
    <section className="bflg-form-section" id="bflg-form" ref={formRef}>
      <div className="bflg-card">
        <span className="bflg-card__eyebrow">{t(`${KEY}Badge`)}</span>
        <h2 className="bflg-card__title">{t(`${KEY}FormTitle`)}</h2>
        <p className="bflg-card__stock">{t(`${KEY}StockNote`)}</p>
        {remaining ? (
          <>
            <form className="bflg-form" onSubmit={handleSubmit} noValidate>
              <label className="bflg-field">
                <span>{t(`${KEY}FieldName`)}</span>
                <input
                  name="firstName"
                  type="text"
                  required
                  maxLength={255}
                  autoComplete="given-name"
                  value={values.firstName}
                  onChange={handleChange}
                  aria-invalid={Boolean(errors.firstName)}
                />
                {errors.firstName && <em className="bflg-error">{t(errors.firstName)}</em>}
              </label>
              <label className="bflg-field">
                <span>{t(`${KEY}FieldEmail`)}</span>
                <input
                  name="email"
                  type="email"
                  required
                  maxLength={254}
                  autoComplete="email"
                  inputMode="email"
                  value={values.email}
                  onChange={handleChange}
                  aria-invalid={Boolean(errors.email)}
                />
                {errors.email && <em className="bflg-error">{t(errors.email)}</em>}
              </label>
              <button className="bflg-btn" type="submit" disabled={submitting}>
                {submitting ? t(`${KEY}Submitting`) : t(`${KEY}Cta`)}
                {!submitting && <span aria-hidden="true">→</span>}
              </button>
              {status === 'error' && <em className="bflg-error">{t(`${KEY}ErrServer`)}</em>}
              <p className="bflg-card__note">{t(`${KEY}FormSub`)}</p>
              <p className="bflg-consent">
                {t(`${KEY}Consent`)}{' '}
                <CustomLink to="/policy">{t(`${KEY}ConsentPolicy`)}</CustomLink>
              </p>
            </form>
            <p className="bflg-card__deadline">{t(`${KEY}DeadlineLabel`)}</p>
            <Countdown remaining={remaining} t={t} />
          </>
        ) : (
          <p className="bflg-card__deadline">{t(`${KEY}DeadlineClosed`)}</p>
        )}
      </div>
    </section>
  );
};

const BlackFridayLeadGen = () => {
  const { t } = useTranslation();
  const reviewStats = useReviewStats();
  const formRef = useRef(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    formRef.current?.querySelector('input')?.focus({ preventScroll: true });
  };

  const score = reviewStats ? Number(reviewStats.average).toFixed(1) : null;

  return (
    <>
      <BfHeader />
    <main className="bflg">
      <Helmet>
        <title>{t(`${KEY}MetaTitle`)}</title>
        <meta name="description" content={t(`${KEY}MetaDesc`)} />
        <meta name="robots" content="noindex" />
      </Helmet>

      <section className="bflg-hero">
        <div className="bflg-hero__head">
          <h1>
            {t(`${KEY}H1a`)} <em>{t(`${KEY}H1b`)}</em>
          </h1>
          <p className="bflg-hero__kicker">{t(`${KEY}HeroKicker`)}</p>
          {reviewStats && (
            <p className="bflg-hero__reviews">
              <Stars />
              {t(`${KEY}ReviewLine`, { score, count: reviewStats.count })}
            </p>
          )}
        </div>
        <SignupForm formRef={formRef} />
        <div className="bflg-hero__body">
          <p className="bflg-hero__sub">{t(`${KEY}Sub`)}</p>
          <ul className="bflg-checks">
            {HERO_BULLETS.map((bullet) => (
              <li key={bullet}>{t(`${KEY}${bullet}`)}</li>
            ))}
          </ul>
        </div>
        <figure className="bflg-hero__photo">
          <img src={HeroPhoto} alt={t(`${KEY}HeroPhotoAlt`)} width="1000" height="950" loading="lazy" />
          <figcaption>
            {t(`${KEY}HeroPhotoLine`)} <em>{t(`${KEY}HeroPhotoEmph`)}</em>
          </figcaption>
        </figure>
      </section>

      <Usps onCta={scrollToForm} />

      {reviewStats && (
        <section className="bflg-section bflg-proof">
          <span className="bflg-label">{t(`${KEY}ProofLabel`)}</span>
          <h2>{t(`${KEY}ProofHeading`, { score })}</h2>
          <p>{t(`${KEY}ProofSub`, { count: reviewStats.count })}</p>
        </section>
      )}

      <div className="bflg-marquee" aria-label={t(`${KEY}ProofLabel`)}>
        <div className="bflg-marquee__track">
          {[0, 1].map((copy) => (
            <div className="bflg-marquee__group" key={copy} aria-hidden={copy === 1}>
              {MARQUEE_REVIEWS.map((review) => (
                <React.Fragment key={review}>
                  <span className="bflg-marquee__item">{t(review)}</span>
                  <span className="bflg-marquee__dot" aria-hidden="true">·</span>
                </React.Fragment>
              ))}
              {reviewStats && (
                <>
                  <span className="bflg-marquee__item bflg-marquee__item--rating">
                    {t('ReviewRating', { rating: score })}
                  </span>
                  <span className="bflg-marquee__dot" aria-hidden="true">·</span>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <section className="bflg-section bflg-perks">
        <span className="bflg-label">{t(`${KEY}PerksLabel`)}</span>
        <h2>{t(`${KEY}PerksHeading`)}</h2>
        <ol className="bflg-perks__list">
          {PERKS.map((perk, index) => (
            <li className="bflg-perk" key={perk}>
              <span className="bflg-perk__tag">{`0${index + 1} / ${t(`${KEY}${perk}Tag`)}`}</span>
              <h3>{t(`${KEY}${perk}Title`)}</h3>
              <p>{t(`${KEY}${perk}Body`)}</p>
            </li>
          ))}
        </ol>
        <button className="bflg-btn bflg-btn--inline" type="button" onClick={scrollToForm}>
          {t(`${KEY}Cta`)}
          <span aria-hidden="true">→</span>
        </button>
      </section>

      <GiftRoll />

      <section className="bflg-section bflg-section--navy bflg-product">
        <div className="bflg-product__copy">
          <span className="bflg-label bflg-label--light">{t(`${KEY}ProductLabel`)}</span>
          <h2>{t(`${KEY}ProductHeading`)}</h2>
          <p className="bflg-section__lead">{t(`${KEY}ProductBody`)}</p>
          <ul className="bflg-checks bflg-checks--light bflg-checks--columns">
            {PRODUCT_POINTS.map((point) => (
              <li key={point}>{t(`${KEY}${point}`)}</li>
            ))}
          </ul>
        </div>
        <video
          className="bflg-product__video"
          src={FormulaVideo}
          poster={FormulaPoster}
          aria-label={t(`${KEY}HeroImgAlt`)}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
        />
      </section>

      <section className="bflg-section bflg-founders">
        <img src={FoundersImage} alt={t(`${KEY}FoundersImgAlt`)} width="480" height="480" loading="lazy" />
        <div>
          <span className="bflg-label">{t(`${KEY}FoundersLabel`)}</span>
          <h2>{t(`${KEY}FoundersHeading`)}</h2>
          <p>{t(`${KEY}FoundersBody`)}</p>
          <p className="bflg-founders__sign">{t(`${KEY}FoundersSign`)}</p>
          <button className="bflg-btn bflg-btn--inline" type="button" onClick={scrollToForm}>
            {t(`${KEY}Cta`)}
            <span aria-hidden="true">→</span>
          </button>
        </div>
      </section>

      <section className="bflg-section">
        <span className="bflg-label">{t(`${KEY}HowLabel`)}</span>
        <h2>{t(`${KEY}HowHeading`)}</h2>
        <ol className="bflg-grid bflg-grid--3 bflg-steps">
          {HOW_STEPS.map((step) => (
            <li className="bflg-tile" key={step}>
              <h3>{t(`${KEY}${step}Title`)}</h3>
              <p>{t(`${KEY}${step}Body`)}</p>
            </li>
          ))}
        </ol>
        <button className="bflg-btn bflg-btn--inline" type="button" onClick={scrollToForm}>
          {t(`${KEY}Cta`)}
          <span aria-hidden="true">→</span>
        </button>
      </section>

      <section className="bflg-section bflg-faq">
        <h2>{t(`${KEY}FaqHeading`)}</h2>
        {FAQS.map((faq) => (
          <details key={faq}>
            <summary>{t(`${KEY}${faq}Q`)}</summary>
            <p>{t(`${KEY}${faq}A`)}</p>
          </details>
        ))}
      </section>

      <section className="bflg-section bflg-final">
        <h2>{t(`${KEY}FinalHeading`)}</h2>
        <button className="bflg-btn" type="button" onClick={scrollToForm}>
          {t(`${KEY}Cta`)}
          <span aria-hidden="true">→</span>
        </button>
        <p className="bflg-legal">{t(`${KEY}Legal`)}</p>
      </section>
    </main>
    </>
  );
};

export default BlackFridayLeadGen;
